import styles from "./chat-input.module.scss";
import { ReactComponent as Smile } from "@assets/svg/smile.svg";
import { ReactComponent as Send } from "@assets/svg/message.svg";
import ReplyDialog from "./components/reply-dialog";
import { replyContext } from "@components/chat";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import useReduxState from "@hooks/useReduxState";
import { ImessageInput } from "@api/types/chat";
import { useMutation } from "@apollo/client";
import { SEND_MESSAGE } from "@graphql/Mutations";
import { SocketContext } from "@context/socket";
import { toastErrorMessage } from "@utils/utils";
import EmojiDialog from "./components/emoji-dialog";

export default function ChatInput(): JSX.Element {
  const { reply, resetReply } = useContext(replyContext);
  const socket = useContext(SocketContext);
  const [user] = useReduxState((state) => state.userData);
  const [message, setMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState<boolean>(false);
  const [showEmojis, setShowEmojis] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [send] = useMutation(SEND_MESSAGE, {
    onCompleted: (data) => {
      socket.emit("New-Message", data.sendMessage);
      setSendingMessage(false);
      setMessage("");
      setShowEmojis(false);
      resetReply();
    },
    onError: (e) => {
      toastErrorMessage(e.message);
      setSendingMessage(false);
      resetReply();
    },
  });

  useEffect(() => {
    if (reply.id !== "" && inputRef.current) {
      inputRef.current.focus();
    }
  }, [reply]);

  const sendMessage = useCallback(async () => {
    if (!sendingMessage) {
      if (message !== "") {
        setSendingMessage(true);
        const input: ImessageInput = {
          userId: user.id,
          message,
          ...(reply.id !== "" ? { reply: reply.id } : {}),
        };
        await send({
          variables: { ...input },
        });
      } else {
        toastErrorMessage("Please put a valid input");
      }
    }
  }, [message]);

  const addEmoji = useCallback(
    (emoji: string) => {
      const currentMessage = message;
      setMessage(currentMessage + emoji);
    },
    [message]
  );

  const handleEnterKey = useCallback(
    async (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.code === "Enter") {
        await sendMessage();
      }
    },
    [message]
  );
  return (
    <div className={styles.chat_input}>
      <ReplyDialog />
      <EmojiDialog opened={showEmojis} addEmoji={addEmoji} />
      <input
        value={message}
        onChange={(e) => setMessage(e.currentTarget.value)}
        type={"text"}
        ref={inputRef}
        placeholder="Enter your message"
        onKeyPress={handleEnterKey}
      />
      <div className={styles.chat_actions}>
        <Smile onClick={() => setShowEmojis(!showEmojis)} />
        <Send onClick={sendMessage} />
      </div>
    </div>
  );
}
